import { Component, OnInit, Input, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../../../services/language'
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import { Subject } from "rxjs";
import { ago, daysAgo } from 'src/app/utils/date';

@Component({
    selector: 'payment-fees',
    templateUrl: './payment-fees.component.html',
    styleUrls: ['./payment-fees.component.scss'],
    standalone: false
})
export class PaymentFeesComponent implements OnInit {

    // Unique internal identity of the sale terms whose payment fees will be displayed.
    @Input() termsId: number = 0

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    // Communicate payment updates to the parent component.
    @Output() feesChanged: EventEmitter<Object> = new EventEmitter()

    //
    protected fees: Array<any> = []

    // Indicates the user is currently waiving a payment fee.
    protected waiving: boolean = false

    // Unique identity of the fee that is being waived.
    protected feeToWaiveId: number = 0

    //
    private records: number = 0

    //
    private page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    constructor (private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService) {}

    ngOnInit() {
        this.fetch()
    }

    ngOnDestroy() {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.termsId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    /** 
     * Fetch the the pending payments for the current contact.
    */
    private fetch = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        if (!this.termsId || (this.termsId <= 0)) {
            return
        }

        this.loading = true
        if (this.page === 1) {
            this.fees = []
        }
        
        this.master.get(`payments/fees?page=${this.page}&term=${this.termsId}&notStatuses=fully%20paid`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = res.data.records
            this.exhausted = (res.data.next === null)
  
            if (this.page > 1) {
                this.fees = this.fees.concat(res.data.fees)
                return
            }
  
            this.fees = res.data.fees
        })
    }

    /**
     * User has decided to wavie the fee so trigger the reason input to show in the proper spot.
     * @param feeId Unique internal identity of the payment fee being waived.
     */
    protected initWaive = (feeId: number) => {
        if (this.waiving) {
            this.waiving = false
            this.feeToWaiveId = 0
            return
        }

        this.waiving = true
        this.feeToWaiveId = feeId
    }

    /**
     * Waive the given payment for the given reason.
     * @param feeId Unique internal identity of the payment fee being waived.
     */
    protected waive = (feeId: number): void => {
        if (!feeId || (feeId <= 0)) {
            return
        }

        this.waiving = false
        this.feeToWaiveId = 0

        const input = (document.getElementById(`${feeId}-waive-reason`) as HTMLInputElement)
        if (!input) {
            this.ms.sendMessage("alert", { type: "danger", text: 'Waive reason is required.' });
            return
        }

        const reason = input.value
        if (!reason || (reason.length <= 0)) {
            this.ms.sendMessage("alert", { type: "danger", text: 'Waive reason is required.' });
            return
        }

        this.master.patch(`payments/fees/${feeId}/waive`, {reason}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.reset()
            this.fetch()

            this.feesChanged.emit({waived: false, restored: true})
        })
    }

    /**
     * Restore a preveiously waived payment fee.
     * @param feeId Unique internal identity of the payment fee being restored.
     */
    protected restore = (feeId: number): void => {
        if (!feeId || (feeId <= 0)) {
            return
        }

        this.master.patch(`payments/fees/${feeId}/restore`, {}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.reset()
            this.fetch()

            this.feesChanged.emit({waived: false, restored: true})
        })
    }

    protected dueToClass = (due: string): string => {
        return ago(due)
    }

    protected daysPastDue = (due: string): number => {
        return daysAgo(due)
    }
}
